import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Heart, ChevronLeft, ChevronRight } from "lucide-react";
import { useShop } from "../contexts/ShopContext";
import { formatPrice } from "../lib/format";

export default function ProductCard({ product }) {
  const { toggleWishlist, inWishlist } = useShop();
  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);
  const imgs = product.images?.length ? product.images : [];
  const liked = inWishlist(product.product_id);
  const soldOut = (product.stock ?? 0) <= 0;

  // Auto-cycle images on hover — 1.2s interval per image
  useEffect(() => {
    if (!hovered || imgs.length < 2) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % imgs.length);
    }, 1200);
    return () => clearInterval(timerRef.current);
  }, [hovered, imgs.length]);

  const goPrev = (e) => { e.preventDefault(); setIdx((i) => (i - 1 + imgs.length) % imgs.length); };
  const goNext = (e) => { e.preventDefault(); setIdx((i) => (i + 1) % imgs.length); };

  return (
    <div className="group relative" data-testid={`product-card-${product.product_id}`}>
      <Link to={`/product/${product.product_id}`}>
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => { setHovered(false); setIdx(0); }}
          className="relative aspect-[3/4] overflow-hidden bg-[#141414]"
        >
          {imgs.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={product.name}
              loading="lazy"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0"} ${soldOut ? "grayscale opacity-70" : ""}`}
            />
          ))}
          {soldOut && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="px-4 py-2 bg-black/80 text-gold text-[10px] uppercase tracking-[0.3em] border border-[#C8A97E]" data-testid={`sold-out-badge-${product.product_id}`}>Sold Out</span>
            </div>
          )}

          {imgs.length > 1 && !soldOut && (
            <>
              {/* Manual arrows — reveal on hover */}
              <button
                onClick={goPrev}
                aria-label="Previous image"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm text-gold opacity-0 group-hover:opacity-100 hover:bg-black/70 transition-opacity flex items-center justify-center"
                data-testid={`card-prev-${product.product_id}`}
              ><ChevronLeft size={14} strokeWidth={1.5} /></button>
              <button
                onClick={goNext}
                aria-label="Next image"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm text-gold opacity-0 group-hover:opacity-100 hover:bg-black/70 transition-opacity flex items-center justify-center"
                data-testid={`card-next-${product.product_id}`}
              ><ChevronRight size={14} strokeWidth={1.5} /></button>

              {/* Progress bars indicating current slide */}
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                {imgs.map((src, i) => (
                  <span
                    key={src}
                    className={`h-[2px] transition-all duration-500 ${i === idx ? "w-8 bg-[#C8A97E]" : "w-4 bg-white/30"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </Link>
      <button
        onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
        className="absolute top-3 right-3 p-2 text-text hover:text-gold transition-colors z-10"
        data-testid={`wishlist-toggle-${product.product_id}`}
      >
        <Heart size={18} strokeWidth={1.5} fill={liked ? "#C8A97E" : "none"} color={liked ? "#C8A97E" : "currentColor"} />
      </button>
      <Link to={`/product/${product.product_id}`} className="block mt-5 space-y-1.5">
        <h3 className="font-serif text-lg text-text">{product.name}</h3>
        <p className="text-xs uppercase tracking-[0.2em] text-muted">{product.category}</p>
        <p className="text-sm text-gold">{formatPrice(product.price)}</p>
      </Link>
    </div>
  );
}
